import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MessageCircle, CheckCircle2, Ban, Clock, Sparkles, Bell } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import MilkPot from "@/components/MilkPot";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/sptl1rmt_image%201.png";

const WHATSAPP_URL = "https://wa.me/919819933676?text=Namaste!%20Mujhe%20Divya%20Dhara%20ka%201%20din%20ka%20%E2%82%B984%20trial%20book%20karna%20hai.%20Please%20contact%20karein.%20%F0%9F%A5%9B";

export default function Hero() {
  const { t } = useI18n();
  const pills = [
    { icon: CheckCircle2, text: t.hero.stats.families },
    { icon: Sparkles, text: t.hero.stats.gir },
    { icon: Ban, text: t.hero.stats.zero },
    { icon: Clock, text: t.hero.stats.six },
  ];

  return (
    <section className="relative pt-24 md:pt-28 pb-12 md:pb-16 bg-dd-page overflow-hidden" data-testid="hero-section">
      {/* Soft gold glow top-left */}
      <div className="absolute top-20 left-0 w-[28rem] h-[28rem] opacity-25 pointer-events-none"
           style={{ background: "radial-gradient(circle at top left, #C9A24C 0%, transparent 60%)" }} />
      <div className="absolute bottom-0 right-0 w-[24rem] h-[24rem] opacity-20 pointer-events-none"
           style={{ background: "radial-gradient(circle at bottom right, #E6C97A 0%, transparent 60%)" }} />

      <div className="relative dd-container grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
        {/* Left: text */}
        <div className="text-center lg:text-left">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 dd-label text-dd-gold mx-auto lg:mx-0"
          >
            <span className="w-10 h-px bg-dd-gold" /> {t.hero.tagline}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.08 }}
            className="mt-5 font-devanagari text-3xl md:text-5xl dd-shimmer leading-snug"
            data-testid="hero-hindi-tagline"
          >
            हर बूंद में अमृत
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-5 font-serif text-[2.5rem] sm:text-6xl lg:text-[5rem] leading-[0.95] tracking-tight text-dd-cream"
          >
            {t.hero.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-6 text-base md:text-lg text-dd-cream-muted leading-relaxed max-w-xl mx-auto lg:mx-0"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start"
          >
            <Link
              to="/#delivery"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-b from-dd-gold to-[#8F6E28] hover:from-dd-gold-light hover:to-dd-gold text-dd-ink-dark px-7 py-4 rounded-full font-bold text-base shadow-[0_12px_32px_-8px_rgba(201,162,76,0.55)] transition-all tracking-wide"
              data-testid="hero-subscribe-btn"
            >
              <Sparkles className="w-5 h-5" strokeWidth={2} />
              Check Delivery & Book
            </Link>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2.5 border border-dd-gold/50 bg-transparent hover:bg-dd-gold hover:text-dd-ink-dark text-dd-cream px-7 py-4 rounded-full font-semibold transition-colors"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2} /> WhatsApp Us
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-10 flex flex-wrap items-center gap-2 justify-center lg:justify-start"
          >
            {pills.map((p) => (
              <div key={p.text} className="inline-flex items-center gap-2 bg-white/5 backdrop-blur border border-dd-gold/30 rounded-full px-3.5 py-1.5 text-xs md:text-sm text-dd-cream shadow-sm">
                <p.icon className="w-4 h-4 text-dd-gold" strokeWidth={2} /> {p.text}
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-6 flex items-center gap-2 text-dd-gold text-sm justify-center lg:justify-start"
            data-testid="hero-live-counter"
          >
            <Bell className="w-4 h-4 animate-pulse" /> <span>{t.hero.live_counter}</span>
          </motion.div>
        </div>

        {/* Right: Real brass-lota photo with milk-pour animation — signature hero */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full flex flex-col items-center justify-center py-4"
        >
          <MilkPot />
        </motion.div>
      </div>
    </section>
  );
}
